export type TranslationOptions<T extends boolean = false> = {
  strict?: T;
  [key: string]: string | null | undefined | T | undefined;
};

export type TranslateFn = <T extends boolean = false>(
  key: string,
  options?: TranslationOptions<T>,
) => T extends true ? string | undefined : string;

/**
 * Replaces `{key}`, `%KEY%`, `%s`, and bare `%` placeholders in a translation with
 * values from `options`.
 *
 * `{key}` placeholders use the matching option key. A value keeps a single leading
 * space, unless the placeholder is inside quotes (`"{key}"` stays tight). If a
 * placeholder has no matching value (null/undefined or missing), it is hidden
 * from the output along with any leading whitespace.
 *
 * `%KEY%` placeholders use the matching option key and are replaced inline.
 *
 * `%s` and bare `%` placeholders are filled in order from remaining option values
 * (excluding `strict` and keys used by other placeholders). A bare `%` is only
 * consumed while such a value remains; otherwise it is kept as-is, so copy like
 * "Få 10% rabatt" is left untouched when no options are passed.
 */

const QUOTES = new Set(['"', "'", "“", "”", "‘", "’", "«", "»", "„", "‚"]);

export default function interpolate<T extends boolean = false>(
  translation: string,
  options?: TranslationOptions<T>,
): T extends true ? string | undefined : string {
  if (options?.strict) return translation;

  const namedKeys = new Set([
    ...[...translation.matchAll(/{(\w+)}/g)].map(([, key]) => key),
    ...[...translation.matchAll(/%([A-Z][A-Z0-9_]*)%/g)].map(([, key]) => key),
  ]);

  const positionalValues = Object.entries(options ?? {})
    .filter(([key]) => key !== "strict" && !namedKeys.has(key))
    .map(([, value]) => value);

  let positionalIndex = 0;

  const nextPositionalValue = () => {
    const value = positionalValues[positionalIndex++];
    return typeof value === "string" ? value : "";
  };

  const getStringOption = (key: string) => {
    const value = options?.[key];
    return typeof value === "string" ? value : "";
  };

  return translation
    .replace(
      /(\s*){(\w+)}/g,
      (match, _space: string, key: string, offset: number) => {
        const value = getStringOption(key);
        if (!value) return "";
        const before = translation[offset - 1];
        const after = translation[offset + match.length];
        if (before && QUOTES.has(before) && after && QUOTES.has(after))
          return value;
        return match.startsWith(" ") || match.startsWith("\n")
          ? ` ${value}`
          : value;
      },
    )
    .replace(/%([A-Z][A-Z0-9_]*)%/g, (_match, key: string) =>
      getStringOption(key),
    )
    .replace(/%s/g, nextPositionalValue)
    .replace(/%/g, (match) =>
      positionalIndex < positionalValues.length ? nextPositionalValue() : match,
    )
    .trim();
}
